var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ResourceProperty } from './ResourceProperty.js';
import { Bkper } from './Bkper.js';
import * as ConnectionService from '../service/connection-service.js';
import { Integration } from './Integration.js';
/**
 * This class defines a Connection from an [[User]] to an external service.
 *
 * @public
 */
export class Connection extends ResourceProperty {
    constructor(payload, config) {
        super(payload);
        this.config = config;
    }
    /** @internal */
    getConfig() {
        return this.config || Bkper.globalConfig;
    }
    /**
     * Gets the id of the Connection.
     *
     * @returns The Connection's id
     */
    getId() {
        return this.payload.id;
    }
    /**
     * Gets the agentId of the Connection.
     *
     * @returns The Connection's agentId
     */
    getAgentId() {
        return this.payload.agentId;
    }
    /**
     * Sets the Connection agentId.
     *
     * @param agentId - The Connection agentId
     *
     * @returns The Connection, for chaining
     */
    setAgentId(agentId) {
        this.payload.agentId = agentId;
        return this;
    }
    /**
     * Gets the name of the Connection.
     *
     * @returns The Connection name
     */
    getName() {
        return this.payload.name;
    }
    /**
     * Gets the logo of the Connection.
     *
     * @returns The Connection logo
     */
    getLogo() {
        return this.payload.logo;
    }
    /**
     * Gets the date when the Connection was added.
     *
     * @returns The Connection add date in milliseconds
     */
    getDateAddedMs() {
        return this.payload.dateAddedMs;
    }
    /**
     * Gets the email of the owner of the Connection.
     *
     * @returns The Connection owner's email
     */
    getEmail() {
        return this.payload.email;
    }
    /**
     * Sets the name of the Connection.
     *
     * @param name - The name of the Connection
     *
     * @returns The Connection, for chaining
     */
    setName(name) {
        this.payload.name = name;
        return this;
    }
    /**
     * Sets the universal unique identifier of the Connection.
     *
     * @param uuid - The universal unique identifier of the Connection
     *
     * @returns The Connection, for chaining
     */
    setUUID(uuid) {
        this.payload.uuid = uuid;
        return this;
    }
    /**
     * Gets the universal unique identifier of this Connection.
     *
     * @returns The Connection's universal unique identifier name
     */
    getUUID() {
        return this.payload.uuid;
    }
    /**
     * Gets the type of the Connection.
     *
     * @returns The Connection type
     */
    getType() {
        return this.payload.type;
    }
    /**
     * Sets the Connection type.
     *
     * @param type - The Connection type
     *
     * @returns The Connection, for chaining
     */
    setType(type) {
        this.payload.type = type;
        return this;
    }
    /**
     * Cleans any token property stored in the Connection.
     */
    clearTokenProperties() {
        this.getPropertyKeys()
            .filter(key => key.includes('token'))
            .forEach(key => this.deleteProperty(key));
    }
    /**
     * Gets the existing [[Integrations]] on the Connection.
     *
     * @returns The existing Integration objects
     */
    getIntegrations() {
        return __awaiter(this, void 0, void 0, function* () {
            const id = this.getId();
            if (!id) {
                return [];
            }
            const integrationsPlain = yield ConnectionService.listIntegrations(id, this.getConfig());
            const integrations = integrationsPlain.map(i => new Integration(i, this.config));
            return integrations;
        });
    }
    /**
     * Performs create new Connection.
     *
     * @returns The created Connection, for chaining
     */
    create() {
        return __awaiter(this, void 0, void 0, function* () {
            this.payload = yield ConnectionService.createConnection(this.payload, this.getConfig());
            return this;
        });
    }
    /**
     * Performs update Connection.
     *
     * @returns The updated Connection object
     */
    update() {
        return __awaiter(this, void 0, void 0, function* () {
            this.payload = yield ConnectionService.updateConnection(this.payload, this.getConfig());
            return this;
        });
    }
    /**
     * Performs remove Connection.
     *
     * @returns The removed Connection object
     */
    remove() {
        return __awaiter(this, void 0, void 0, function* () {
            const connectionId = this.getId();
            if (connectionId) {
                this.payload = yield ConnectionService.deleteConnection(connectionId, this.getConfig());
            }
            return this;
        });
    }
}
//# sourceMappingURL=Connection.js.map